import React, {
    useState,
    useEffect,
    useCallback,
    forwardRef,
    useImperativeHandle,
    useRef
} from 'react';
import {
    Grid,
    TextField,
    Button,
    Typography,
    Box,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    TableContainer,
    TableSortLabel, FormControl,
    FormHelperText
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import UndoIcon from '@material-ui/icons/Undo';
import ClearIcon from '@material-ui/icons/Clear';
import { useMemo } from 'react';
import { validateNPI } from './validateNPI';


// Extracted dialog component
const ChangesSummaryDialog = ({ open, onClose, changesSummary }) => (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
    >
        <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Review Changes</Typography>
            </Box>
        </DialogTitle>
        <DialogContent>
            {changesSummary.length > 0 ? (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ fontWeight: 'bold' }}>Field</TableCell>
                            <TableCell style={{ fontWeight: 'bold' }}>Original Value</TableCell>
                            <TableCell style={{ fontWeight: 'bold' }}>New Value</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {changesSummary.map((change, index) => (
                            <TableRow key={index}>
                                <TableCell>{change.field}</TableCell>
                                <TableCell>{change.originalValue || 'Not Set'}</TableCell>
                                <TableCell>{change.newValue || 'Not Set'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : (
                <Typography>No changes to review.</Typography>
            )}
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} color="primary">
                Close
            </Button>
        </DialogActions>
    </Dialog>
);

const FacilityGeneralEdit = forwardRef(({
    facility, facilityType,
    onChange,
    onValidationFunction,
    onSectionSaveLocal,
    onSectionDiscardChanges,
    onSectionHasChanges
}, ref) => {
    // Add a ref to track if we're in the middle of an edit
    const isEditingRef = useRef(false);
    // Add a ref to prevent automatic change detection during manual changes
    const manualChangeRef = useRef(false);
    // Add a ref to store the truly original data that never changes
    const initialDataRef = useRef({
        facility: null,
        altNpis: []
    });

    // Basic state
    const [formData, setFormData] = useState({
        faclFacilityName: '',
        faclTaxId: '',
        prvNpi: '',
        fLob: '',
        altNpis: []
    });

    const [searchText, setSearchText] = useState('');
    const [originalData, setOriginalData] = useState(null);
    const [errors, setErrors] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    const [localSaveMessage, setLocalSaveMessage] = useState("");
    const [isValidated, setIsValidated] = useState(false);
    const [showChangesSummary, setShowChangesSummary] = useState(false);
    const [changesSummary, setChangesSummary] = useState([]);

    // State for AltNPIs
    const [altNpis, setAltNpis] = useState([]);
    const [originalAltNpis, setOriginalAltNpis] = useState([]);
    const [altNpisChanged, setAltNpisChanged] = useState(false);
    const [newNpi, setNewNpi] = useState('');

    // Add a reset counter to force re-renders
    const [resetCounter, setResetCounter] = useState(0);

    // Add sorting state for Alt NPI table
    const [sortOrder, setSortOrder] = useState('asc');

    const handleSearchChange = useCallback((e) => {
        setSearchText(e.target.value);
    }, []);


    const searchedAltNpis = useMemo(() => {
        if (!searchText) return altNpis;
        return altNpis.filter(item =>
            (item.panpiNpi || '').toLowerCase().includes(searchText.toLowerCase())
        );
    }, [altNpis, searchText]);


    // Helper function to compare Alt NPIs arrays
    const compareAltNpis = useCallback((npis1, npis2) => {
        if (!npis1 || !npis2) return false;
        if (npis1.length !== npis2.length) return false;

        const values1 = [...npis1].map(item => item.panpiNpi || '').sort();
        const values2 = [...npis2].map(item => item.panpiNpi || '').sort();

        for (let i = 0; i < values1.length; i++) {
            if (values1[i] !== values2[i]) {
                return false;
            }
        }
        return true;
    }, []);


    const validateForm = useCallback((data) => {
        const fieldErrors = {};

        // Validate NPI if present
        if (data.prvNpi) {
            // First check standard NPI format
            const npiValidation = validateNPI(data.prvNpi);
            if (npiValidation.status === 'FAIL') {
                fieldErrors.prvNpi = npiValidation.message;
            }

            // Then check if primary NPI exists in alt NPIs
            const currentAltNpis = altNpis || [];
            const matchingAltNpi = currentAltNpis.find(item => item.panpiNpi === data.prvNpi);
            if (matchingAltNpi) {
                fieldErrors.prvNpi = "Primary NPI cannot match any alternative NPI";
            }
        }

        // Validate alt NPIs (existing code)
        const currentAltNpis = altNpis || [];
        if (currentAltNpis.length > 0) {
            const invalidNpis = currentAltNpis.filter(item => {
                if (!item.panpiNpi) return false;
                const validation = validateNPI(item.panpiNpi);
                return validation.status === 'FAIL';
            });

            if (invalidNpis.length > 0) {
                fieldErrors.altNpis = 'One or more NPIs failed validation';
            }
        }

        return {
            isValid: Object.keys(fieldErrors).length === 0,
            fieldErrors
        };
    }, [altNpis]);

    // Handle field changes
    const handleFieldChange = useCallback((field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Also notify parent component about the change
        if (onChange) {
            onChange(field, value);
        }

        // Clear any error for this field
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    }, [errors, onChange]);

    // Handle NPI change

    const handleNpiChange = useCallback((e) => {
        const newValue = e.target.value;
        console.log("NPI change:", newValue, "Original:", initialDataRef.current?.facility?.prvNpi);

        // Set the editing flag
        isEditingRef.current = true;
        manualChangeRef.current = true;

        // Check if the new primary NPI matches any alternative NPI
        const matchingAltNpi = altNpis.find(item => item.panpiNpi === newValue);
        if (matchingAltNpi) {
            // Set an error for the primary NPI field
            setErrors(prev => ({
                ...prev,
                prvNpi: "Value already in Alt NPI table"
            }));
        } else {
            // Clear any specific NPI error when the field changes
            if (errors.prvNpi) {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.prvNpi;
                    return newErrors;
                });
            }
        }

        // Update the form data regardless of validation
        // This allows the user to see what they typed
        handleFieldChange('prvNpi', newValue);

        // Explicitly check for changes here
        if (initialDataRef.current?.facility && newValue !== initialDataRef.current.facility.prvNpi) {
            console.log("Setting hasChanges to true in handleNpiChange");
            setHasChanges(true);
            if (onSectionHasChanges) {
                onSectionHasChanges(true);
            }
        } else if (initialDataRef.current?.facility &&
            newValue === initialDataRef.current.facility.prvNpi &&
            compareAltNpis(altNpis, initialDataRef.current.altNpis)) {
            // Only reset hasChanges if NPI matches original and Alt NPIs match original
            setHasChanges(false);
            setAltNpisChanged(false);
            if (onSectionHasChanges) {
                onSectionHasChanges(false);
            }
        }

        // Clear the flags after a longer delay
        setTimeout(() => {
            isEditingRef.current = false;
            manualChangeRef.current = false;
        }, 500);
    }, [handleFieldChange, compareAltNpis, altNpis, onSectionHasChanges, errors]);

    const handleReset = useCallback((e) => {
        if (e) e.preventDefault();

        if (initialDataRef.current.facility) {
            console.log("RESET: Resetting form to initial state");
            console.log("RESET: Initial Alt NPIs:", initialDataRef.current.altNpis);
            console.log("RESET: Initial NPI:", initialDataRef.current.facility.prvNpi);

            // Set editing flag to prevent automatic change detection
            isEditingRef.current = true;
            manualChangeRef.current = true;

            // Reset form state to initial data
            const resetFormData = JSON.parse(JSON.stringify(initialDataRef.current.facility));
            setFormData(resetFormData);

            // Reset AltNPIs to initial - create a fresh deep copy
            const resetAltNpis = JSON.parse(JSON.stringify(initialDataRef.current.altNpis));
            console.log("RESET: Setting Alt NPIs to:", resetAltNpis);
            console.log("RESET: Setting NPI to:", resetFormData.prvNpi);

            // Force a complete reset by creating new objects
            setAltNpis(resetAltNpis);
            setOriginalAltNpis(resetAltNpis);
            setNewNpi('');
            setAltNpisChanged(false);

            // Reset other state
            setErrors({});
            setHasChanges(false);

            // Force a re-render
            setResetCounter(prev => prev + 1);

            // Notify parent components about the reset values
            if (onChange) {
                onChange('prvNpi', resetFormData.prvNpi);
                onChange('altNpiList', resetAltNpis);
            }

            if (onSectionDiscardChanges) {
                onSectionDiscardChanges({
                    pristineObject: initialDataRef.current.facility,
                    modifiedObject: null
                });
            }

            if (onSectionHasChanges) {
                onSectionHasChanges(false);
            }

            // Show a message
            setLocalSaveMessage("All changes discarded");
            setTimeout(() => setLocalSaveMessage(""), 3000);

            // Clear the editing flag after a delay
            setTimeout(() => {
                isEditingRef.current = false;
                manualChangeRef.current = false;
            }, 500); // Longer timeout
        }
    }, [onChange, onSectionDiscardChanges, onSectionHasChanges]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
        getFormData: () => {
            // Create a combined object with current form data and altNpis
            const currentData = {
                ...formData,
                altNpis: altNpis,
                altNpiList: altNpis
            };

            // Validate first if not already validated
            let isValid = isValidated;
            if (!isValid) {
                const validationResult = validateForm(currentData);
                isValid = validationResult.isValid;

                if (!isValid) {
                    setErrors(validationResult.fieldErrors);
                }

                setIsValidated(isValid);
            }

            return {
                pristineObject: initialDataRef.current.facility,
                modifiedObject: hasChanges ? currentData : null,
                isValid: isValid
            };
        },
        validateForm: () => {
            // Create a combined object with current form data and altNpis
            const dataToValidate = {
                ...formData,
                altNpis: altNpis
            };

            const { isValid, fieldErrors } = validateForm(dataToValidate);

            if (!isValid) {
                setErrors(fieldErrors);
            }

            setIsValidated(isValid);
            return isValid;
        },
        resetToOriginal: handleReset
    }), [formData, altNpis, isValidated, hasChanges, validateForm, handleReset]);


    // Modify handleAddAltNpi to ensure NPI consistency

    const handleAddAltNpi = useCallback(() => {
        console.log("Adding Alt NPI. Current primary NPI:", formData.prvNpi);
        console.log("Original primary NPI:", initialDataRef.current.facility.prvNpi);

        // Check against the current form NPI
        if (newNpi === formData.prvNpi) {
            setErrors(prev => ({
                ...prev,
                altNpis: "Value cannot be the same as the primary NPI"
            }));
            return;
        }

        const validation = validateNPI(newNpi);
        if (validation.status === 'FAIL') {
            // Use altNpis error for NPI format issues too
            setErrors(prev => ({
                ...prev,
                altNpis: validation.message
            }));
            return;
        }

        if (altNpis.some(item => item.panpiNpi === newNpi)) {
            setErrors(prev => ({
                ...prev,
                altNpis: "This is a duplicate Alt NPI"
            }));
            return;
        }

        // Clear any altNpis errors before proceeding
        if (errors.altNpis) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.altNpis;
                return newErrors;
            });
        }

        // Set the editing flag
        isEditingRef.current = true;
        manualChangeRef.current = true;

        // Create the new NPI item with a unique panpiId
        const newItem = { panpiId: Date.now(), panpiNpi: newNpi, bypassAuditFlag: "N" };

        // Update the altNpis array
        const updatedNpis = [...altNpis, newItem];
        setAltNpis(updatedNpis);
        setNewNpi('');

        // CRITICAL: Force hasChanges and altNpisChanged to true
        console.log("Setting hasChanges to true after adding Alt NPI");
        setAltNpisChanged(true);
        setHasChanges(true);

        // Notify parent component - IMPORTANT: This must be true
        if (onSectionHasChanges) {
            onSectionHasChanges(true);
        }

        // Also notify parent component about the data change AND explicitly include current NPI
        if (onChange) {
            onChange('altNpiList', updatedNpis);
            // CRITICAL: Ensure the parent has the correct NPI value
            onChange('prvNpi', formData.prvNpi);
        }

        // Clear the flags after a longer delay
        setTimeout(() => {
            isEditingRef.current = false;
            manualChangeRef.current = false;
        }, 500);
    }, [altNpis, newNpi, onChange, onSectionHasChanges, formData.prvNpi]);

    const handleRemoveAltNpi = useCallback((panpiId) => {
        // Set the editing flag
        isEditingRef.current = true;
        manualChangeRef.current = true;

        console.log("Removing Alt NPI with ID:", panpiId, "Current altNpis:", altNpis);

        // Create a new array without the item with the specified ID
        const updatedNpis = altNpis.filter(item => item.panpiId !== panpiId);

        console.log("Updated altNpis after removal:", updatedNpis);

        // Update state with the new array
        setAltNpis(updatedNpis);

        // CRITICAL: Force hasChanges and altNpisChanged to true
        console.log("Setting hasChanges to true after removing Alt NPI");
        setAltNpisChanged(true);
        setHasChanges(true);

        // Notify parent component - IMPORTANT: This must be true
        if (onSectionHasChanges) {
            onSectionHasChanges(true);
        }

        // Also notify parent component about the data change
        if (onChange) {
            onChange('altNpiList', updatedNpis);
        }

        // Clear the flags after a longer delay
        setTimeout(() => {
            isEditingRef.current = false;
            manualChangeRef.current = false;
        }, 500);
    }, [altNpis, onChange, onSectionHasChanges]);

    const handleKeyPress = useCallback((e) => {
        if (e.key === 'Enter' && newNpi && /^\d{10}$/.test(newNpi)) {
            handleAddAltNpi();
            e.preventDefault();
        }
    }, [newNpi, handleAddAltNpi]);

    // Handler for sorting Alt NPIs
    const handleSort = useCallback(() => {
        // Toggle sort order
        const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        setSortOrder(newOrder);

        // Sort the Alt NPIs array - add null checks to prevent errors
        const sortedNpis = [...altNpis].sort((a, b) => {
            // Get the values, defaulting to empty string if undefined
            const valueA = a.panpiNpi || '';
            const valueB = b.panpiNpi || '';

            if (newOrder === 'asc') {
                return valueA.localeCompare(valueB);
            } else {
                return valueB.localeCompare(valueA);
            }
        });

        setAltNpis(sortedNpis);
    }, [altNpis, sortOrder]);

    const calculateChanges = useCallback(() => {
        if (!initialDataRef.current.facility) {
            return [];
        }

        const changes = [];

        // Check NPI change
        if (formData.prvNpi !== initialDataRef.current.facility.prvNpi) {
            changes.push({
                field: 'NPI',
                originalValue: initialDataRef.current.facility.prvNpi || 'Not Set',
                newValue: formData.prvNpi || 'Not Set'
            });
        }

        // Check alt NPIs changes
        if (altNpisChanged) {
            // Get all initial NPIs
            const initialNpis = initialDataRef.current.altNpis || [];
            const initialNpiValues = initialNpis.map(item => item.panpiNpi).filter(Boolean);

            // Get all current NPIs
            const currentNpiValues = altNpis.map(item => item.panpiNpi).filter(Boolean);

            // Find added NPIs (in current but not in initial)
            const addedNpis = currentNpiValues.filter(npi => !initialNpiValues.includes(npi));

            // Find removed NPIs (in initial but not in current)
            const removedNpis = initialNpiValues.filter(npi => !currentNpiValues.includes(npi));

            // Create a descriptive change message
            let changeDescription = '';
            if (addedNpis.length > 0) {
                changeDescription += `Added: ${addedNpis.join(', ')}`;
            }
            if (removedNpis.length > 0) {
                if (changeDescription) changeDescription += '\n';
                changeDescription += `Removed: ${removedNpis.join(', ')}`;
            }

            // If there are no specific additions/removals but the arrays are different,
            // show the complete before/after
            if (!changeDescription && !compareAltNpis(initialNpis, altNpis)) {
                const initialAltNpisStr = initialNpiValues.join(', ');
                const currentAltNpisStr = currentNpiValues.join(', ');

                changes.push({
                    field: 'Alternative NPIs',
                    originalValue: initialAltNpisStr || 'None',
                    newValue: currentAltNpisStr || 'None'
                });
            } else if (changeDescription) {
                // Show the specific changes
                changes.push({
                    field: 'Alternative NPIs Changes',
                    originalValue: initialNpiValues.join(', ') || 'None',
                    newValue: changeDescription
                });
            }
        }

        return changes;
    }, [formData, altNpisChanged, altNpis, compareAltNpis]);
    // Modify the newNpi change handler
    const handleNewNpiChange = (e) => {
        const value = e.target.value;
        setNewNpi(value);

        // Clear errors when input changes, unless still invalid
        if (errors.altNpis) {
            // Only clear if the new value passes all checks
            const isDuplicate = altNpis.some(item => item.panpiNpi === value);
            const isPrimaryNpi = value === formData.prvNpi;
            const isInvalidFormat = validateNPI(value).status === 'FAIL';

            if (!isDuplicate && !isPrimaryNpi && !isInvalidFormat) {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.altNpis;
                    return newErrors;
                });
            }
        }
    };
    // Handler for the "Review Changes" button
    const handleShowChanges = useCallback((e) => {
        if (e) e.preventDefault();

        // Create a combined object with current form data and altNpis
        const dataToValidate = {
            ...formData,
            altNpis: altNpis
        };

        // Validate the form first
        const { isValid, fieldErrors } = validateForm(dataToValidate);

        if (!isValid) {
            setErrors(fieldErrors);
            setLocalSaveMessage("Please fix validation errors before reviewing changes");
            setTimeout(() => setLocalSaveMessage(""), 3000);
            return;
        }

        // Get the current changes
        const changes = calculateChanges();

        if (changes.length === 0) {
            setLocalSaveMessage("No changes to review");
            setTimeout(() => setLocalSaveMessage(""), 3000);
            return;
        }

        // Set changes summary for display
        setChangesSummary(changes);

        // Show changes dialog
        setShowChangesSummary(true);

        // Mark as validated
        setIsValidated(true);

    }, [formData, altNpis, validateForm, calculateChanges]);

    const handleCloseChangesSummary = useCallback(() => {
        setShowChangesSummary(false);
    }, []);


    useEffect(() => {
        if (errors.altNpis && newNpi) {
            // Check if the new value is still a duplicate
            const isDuplicate = altNpis.some(item => item.panpiNpi === newNpi);

            // Only clear the error if it's not a duplicate and not the primary NPI
            if (!isDuplicate && newNpi !== formData.prvNpi) {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.altNpis;
                    return newErrors;
                });
            }
        }
    }, [newNpi, altNpis, formData.prvNpi, errors.altNpis]);

    useEffect(() => {
        if (!facility) return;

        console.log("Facility changed:", facility);

        // If we're in the middle of an edit, don't reset the form data completely
        // but update only what we need to
        if (isEditingRef.current || manualChangeRef.current) {
            // Just update the reference data but don't change current form state
            console.log("In editing mode - not resetting form data");
            return;
        }

        // Normal initialization (not during an edit)
        const initialData = {
            faclFacilityName: facility.faclFacilityName || '',
            faclTaxId: facility.faclTaxId || '',
            prvNpi: facility.prvNpi || '',
            fLob: facility.fLob || '',
            altNpiList: facility.altNpiList || []
        };

        // Initialize AltNPIs from altNpiList
        const initialAltNpis = (facility.altNpiList || []).map(item => ({
            panpiId: item.panpiId,
            panpiNpi: item.panpiNpi,
            bypassAuditFlag: item.bypassAuditFlag || "N"
        }));

        // Store the initial data in the ref - this never changes
        if (!initialDataRef.current.facility) {
            initialDataRef.current = {
                facility: { ...initialData },
                altNpis: JSON.parse(JSON.stringify(initialAltNpis))
            };
            console.log("Stored initial data:", initialDataRef.current);
        }

        setAltNpis(initialAltNpis);
        setOriginalAltNpis(JSON.parse(JSON.stringify(initialAltNpis))); // Deep copy

        setFormData(initialData);
        setOriginalData(initialData);
        setErrors({});
        setHasChanges(false);
        setAltNpisChanged(false);
        setIsValidated(false);
    }, [facility]);
    // Modified effect to check for changes but respect manual changes
    useEffect(() => {
        // Skip if we're in the middle of editing or if there's no original data
        // or if manual change is in progress
        if (isEditingRef.current || manualChangeRef.current || !initialDataRef.current.facility) return;

        // Check if NPI changed from initial value
        const npiChanged = formData.prvNpi !== initialDataRef.current.facility.prvNpi;

        // Check if Alt NPIs changed from initial values
        const altNpisModified = !compareAltNpis(altNpis, initialDataRef.current.altNpis);

        // Determine if anything has changed from initial state
        const changed = npiChanged || altNpisModified;

        console.log("Auto change detection:", {
            npiChanged,
            altNpisModified,
            currentHasChanges: hasChanges
        });

        // Only update if the detected state is different from current state
        if (changed !== hasChanges) {
            console.log("Auto-detection setting hasChanges to:", changed);
            setHasChanges(changed);
            setAltNpisChanged(altNpisModified);

            // Update parent component
            if (onSectionHasChanges) {
                onSectionHasChanges(changed);
            }
        }
    }, [formData.prvNpi, altNpis, hasChanges, compareAltNpis, onSectionHasChanges]);

    // Expose validation function to parent
    useEffect(() => {
        if (onValidationFunction) {
            onValidationFunction(() => {
                // Create a combined object with current form data and altNpis
                const dataToValidate = {
                    ...formData,
                    altNpis: altNpis
                };

                const { isValid, fieldErrors } = validateForm(dataToValidate);

                if (!isValid) {
                    setErrors(fieldErrors);
                }

                setIsValidated(isValid);
                return isValid;
            });
        }
    }, [onValidationFunction, validateForm, formData, altNpis]);

    // Debug logging for state changes
    useEffect(() => {
        console.log("State update - altNpisChanged:", altNpisChanged, "hasChanges:", hasChanges);
    }, [altNpisChanged, hasChanges]);

    // Style for disabled text fields
    const disabledTextFieldStyle = {
        '& .MuiInputBase-root.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.87)',
        },
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#f5f5f5',
        }
    };

    // Style for table headers to match the attached document
    const tableHeaderStyle = {
        backgroundColor: '#f5f5f5',
        color: '#333333',
        fontWeight: 600,
        fontSize: '0.875rem',
        padding: '8px 16px',
        borderBottom: '2px solid #e0e0e0'
    };

    // Add console log to track hasChanges
    console.log("FacilityGeneralEdit rendering with hasChanges:", hasChanges);

    return (
        <>
            <form
                id="facility-edit-form"
                style={{ width: '100%' }}
                noValidate
                onSubmit={(e) => e.preventDefault()}
            >
                <Grid container spacing={3}>
                    {/* First row: Facility Name, TIN, LOB */}
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label={facilityType === 'H' ? 'Hospital Name' : 'Ancillary Name'}
                            value={formData.faclFacilityName || ''}
                            disabled
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            style={disabledTextFieldStyle}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="TIN"
                            value={formData.faclTaxId || ''}
                            disabled
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            style={disabledTextFieldStyle}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="LOB"
                            value={formData.fLob || ''}
                            disabled
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            style={disabledTextFieldStyle}
                        />
                    </Grid>

                    {/* Second row: NPI and Alt NPIs */}
                    <Grid item xs={12} md={4}>

                        <TextField
                            fullWidth
                            label="NPI"
                            value={formData.prvNpi || ''}
                            onChange={handleNpiChange}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                maxLength: 10,
                                pattern: '[0-9]*',
                                inputMode: 'numeric'
                            }}
                            error={!!errors.prvNpi || (formData.prvNpi && validateNPI(formData.prvNpi).status === 'FAIL')}
                            helperText={errors.prvNpi || (formData.prvNpi && validateNPI(formData.prvNpi).status === 'FAIL'
                                ? validateNPI(formData.prvNpi).message : '')}
                            onBlur={() => {
                                // Validate on blur for better UX
                                if (formData.prvNpi) {
                                    const validation = validateNPI(formData.prvNpi);
                                    if (validation.status === 'FAIL') {
                                        setErrors(prev => ({
                                            ...prev,
                                            prvNpi: validation.message
                                        }));
                                    }
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        {/* Alt NPIs section - now same width as other fields */}
                        <Paper style={{
                            padding: '16px',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                        }} key={`alt-npis-${resetCounter}`}>
                            <Typography variant="subtitle1" gutterBottom>
                                Alternative NPIs
                            </Typography>

                            <Box display="flex" mb={2} alignItems="center">

                                <TextField
                                    label="New NPI"
                                    value={newNpi}
                                    onChange={handleNewNpiChange}  // Use the new handler
                                    error={
                                        (!!newNpi && validateNPI(newNpi).status === 'FAIL') ||
                                        !!errors.altNpis
                                    }
                                    helperText={
                                        errors.altNpis ||
                                        (newNpi ? validateNPI(newNpi).message : "")
                                    }
                                    inputProps={{
                                        maxLength: 10,
                                        pattern: '[0-9]*',
                                        inputMode: 'numeric'
                                    }}
                                    variant="outlined"
                                    size="small"
                                    style={{ marginRight: '8px', marginBottom: '2px', marginTop: '2px', flexGrow: 1 }}
                                    onKeyPress={handleKeyPress}
                                    InputProps={{
                                        endAdornment: newNpi ? (
                                            <IconButton
                                                aria-label="clear input"
                                                onClick={() => setNewNpi('')}
                                                edge="end"
                                                size="small"
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        ) : null
                                    }}
                                />
                                <IconButton
                                    color="primary"
                                    onClick={handleAddAltNpi}
                                    disabled={!newNpi || !/^\d{10}$/.test(newNpi)}
                                    aria-label="add"
                                    size="small"
                                >
                                    <AddIcon />
                                </IconButton>
                            </Box>

                            {altNpis.length > 0 ? (
                                <>
                                    <TextField
                                        size="small"
                                        placeholder="Search..."
                                        value={searchText}
                                        onChange={handleSearchChange}
                                        variant="outlined"
                                        fullWidth
                                        margin="dense"
                                        InputProps={{
                                            startAdornment: (
                                                <span style={{ marginRight: 8 }}>🔍</span>
                                            ),
                                            endAdornment: (
                                                <span
                                                    style={{
                                                        cursor: 'pointer',
                                                        marginLeft: 8,
                                                        fontSize: '0.875rem',
                                                        color: '#757575',
                                                        opacity: searchText ? 1 : 0.5
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Prevent triggering the TextField click
                                                        setSearchText('');
                                                    }}
                                                >
                                                    ✕
                                                </span>
                                            ),
                                            style: { fontSize: '0.875rem' }
                                        }}
                                        style={{ marginBottom: '2px' }}
                                    />
                                    <TableContainer component={Paper} variant="outlined" style={{ maxHeight: '200px' }}>
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell style={{ backgroundColor: '#0f197d' }}>
                                                        <TableSortLabel
                                                            active={true}
                                                            direction={sortOrder}
                                                            onClick={handleSort}
                                                        >
                                                            NPI
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell align="right" style={{ backgroundColor: '#0f197d' }}>Action</TableCell>
                                                </TableRow>
                                            </TableHead>

                                            <TableBody>
                                                {searchedAltNpis.map((item, index) => (
                                                    <TableRow key={item.panpiId || index}>
                                                        <TableCell>{item.panpiNpi}</TableCell>
                                                        <TableCell align="right">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleRemoveAltNpi(item.panpiId)}
                                                                aria-label="delete"
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    {searchedAltNpis.length === 0 && (
                                        <Typography color="textSecondary" style={{ marginTop: '8px', textAlign: 'center' }}>
                                            No matching NPIs found
                                        </Typography>
                                    )}
                                </>
                            ) : (
                                <Typography color="textSecondary" style={{ marginTop: '8px' }}>
                                    No alternative NPIs added
                                </Typography>
                            )}

                        </Paper>
                    </Grid>

                    {/* Empty grid item for spacing */}
                    <Grid item xs={12} md={4}>
                        {/* Intentionally left empty for layout purposes */}
                    </Grid>

                    {/* Action Buttons */}
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="center" gap={2}>
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={handleReset}
                                size="large"
                                startIcon={<UndoIcon />}
                                disabled={!hasChanges}
                            >
                                Discard Changes
                            </Button>
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                onClick={handleShowChanges}
                                size="large"
                                disabled={!hasChanges}
                                startIcon={<VisibilityIcon />}
                            >
                                Review Changes
                            </Button>
                        </Box>
                    </Grid>

                    {/* Display local save message if present */}
                    {localSaveMessage && (
                        <Grid item xs={12} style={{ marginTop: '16px', textAlign: 'center' }}>
                            <Typography
                                color={localSaveMessage.includes("fix") ? "error" : "primary"}
                                variant="body2"
                            >
                                {localSaveMessage}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </form>

            {/* Changes Summary Dialog */}
            <ChangesSummaryDialog
                open={showChangesSummary}
                onClose={handleCloseChangesSummary}
                changesSummary={changesSummary}
            />
        </>
    );
});

export default FacilityGeneralEdit;